/*
 * Copyright 2024 NewMedia Centre - Delft University of Technology
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

'use client'

// import dependencies
import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'

// import components
import Icon from '@mdi/react'
import Link from 'next/link'
import Image from 'next/image'

// import assets
import logo from '@/assets/tud-nmc-dark.svg'

// import icons
import { mdiAccount, mdiLogout, mdiClose, mdiMenu, mdiHome } from '@mdi/js'

// import types
import type { User } from '@prisma/client'

export const Sidebar = ({ children, user }: { children: React.ReactNode, user: User | null }) => {
	const [open, setOpen] = useState(false)
	const path = usePathname()

	useEffect(() => {
		setOpen(false)
	}, [path])

	return (
		<aside className={`fixed z-20 flex w-full shrink-0 flex-col gap-6 overflow-hidden border-b border-700 bg-gradient-to-tl from-950 to-900 sm:relative sm:w-64 sm:border-b-0 ${open ? 'h-screen' : ''}`}>
			<div className='flex items-center justify-between p-3'>
				<div className='flex items-center gap-2 sm:hidden'>
					<a href='/' className='flex p-3 text-600 hover:text-white'>
						<Icon path={mdiHome} size={1} />
					</a>
					<a href='https://newmediacentre.tudelft.nl/xr/'>
						<Image alt='NewMedia Centre' src={logo} height={32} />
					</a>
				</div>
				<div className='flex items-center gap-2 sm:w-full'>
					<SidebarUserButton user={user} />
					<button onClick={() => setOpen(x => !x)} className='p-3 text-600 hover:text-white sm:hidden'>
						<Icon path={open ? mdiClose : mdiMenu} size={1} />
					</button>
				</div>
			</div>
			<div className={`${open ? 'flex' : 'hidden'} grow flex-col gap-6 overflow-hidden sm:flex`}>
				{children}
			</div>
		</aside>
	)
}

export const SidebarUserButton = ({ user }: { user: User | null }) => {
	const [hovered, setHovered] = useState(false)

	if (!user) return (
		<a href='/api/auth/oauth' className='flex items-center text-600 hover:text-white sm:w-full'>
			<div className='flex items-center justify-center p-3'>
				<Icon path={mdiAccount} size={1} />
			</div>
			<div className='hidden flex-col items-start sm:flex'>
				<span>Log In</span>
			</div>
		</a>
	)

	return (
		<a href='/api/auth/logout' className='flex items-center text-600 hover:text-white sm:w-full' onMouseOver={() => setHovered(true)} onMouseOut={() => setHovered(false)}>
			<div className='flex items-center justify-center p-3'>
				<Icon path={hovered ? mdiLogout : mdiAccount} size={1} />
			</div>
			{hovered ? (
				<div className='flex flex-col items-start'>
					<span>Log Out</span>
				</div>
			) : (
				<div className='hidden flex-col items-start sm:flex'>
					<span>{user.name}</span>
					<span className='text-xs capitalize'>{user.role.toLowerCase()}</span>
				</div >
			)}
		</a>
	)
}

export const SidebarIconButton = ({ href, icon, label, exact = false }: { href: string, icon: string, label: string, exact?: boolean }) => {
	const pathname = usePathname()

	return (
		<Link href={href} className={'flex h-12 items-center border-white gap-3 px-6 hover:text-white ' + ((exact ? pathname === href : pathname?.startsWith(href)) ? 'border-l-2 ps-[22px] text-white' : 'text-400')}>
			<Icon path={icon} size={1} />
			<span>{label}</span>
		</Link>
	)
}